import { formatResultValue } from 'components/Calculator/utils/formatResultValue';
import { evaluateFormula, getFormulaReferences } from 'components/Calculator/utils/formula';

import type { CalculatorResultField, EvaluatedResult } from 'components/Calculator/config/types';
import type { ResultFormat } from 'components/Calculator/utils/formatResultValue';
import type { FormulaScope } from 'components/Calculator/utils/formula';

/**
 * The Builder panel offers decimal places as a dropdown including 'auto', since
 * an empty number field and a deliberate zero arrive indistinguishable and mean
 * different things. 'auto' is the formatter's own default, which is no value.
 */
const toDecimals = (decimals: CalculatorResultField['decimals']): number | undefined =>
  decimals === undefined || decimals === 'auto' ? undefined : Number(decimals);

const toResultFormat = ({ decimals, showSign, suffixLabel, valueType }: CalculatorResultField): ResultFormat => ({
  decimals: toDecimals(decimals),
  showSign,
  type: valueType,
  unitLabel: suffixLabel,
});

/**
 * Evaluate a calculator's results in order against the reader's input values.
 *
 * Each result that computes successfully adds its key to the scope, so a later
 * result can be written in terms of an earlier one. Several metrics in the
 * Integration Health series are exactly that: Win Rate Lift subtracts one
 * computed win rate from another, Sales Cycle Reduction does the same with two
 * averages, and the retention comparison sits on two computed rates.
 *
 * A result that fails does not stop the ones after it. Any later result that
 * referred to it fails too, reporting the missing key by name, which points the
 * editor at the result that actually broke rather than at a blank panel.
 *
 * @param results - The result fields as configured in Builder, in display order
 * @param inputs - Input keys mapped to the reader's current values
 * @returns One entry per result, in the same order
 */
export const evaluateResults = (results: CalculatorResultField[], inputs: FormulaScope): EvaluatedResult[] => {
  const scope: FormulaScope = { ...inputs };
  const resultKeys = new Set(results.map(({ key }) => key));

  return results.map(result => {
    const { formula, key, label } = result;
    // Derived is read off the formula rather than configured, so an editor
    // cannot mislabel it: a result is derived when it reads another result.
    const derived = getFormulaReferences(formula).some(reference => resultKeys.has(reference));

    if (key in scope) {
      return {
        derived,
        display: '',
        error: `"${key}" is already the key of an input or an earlier result. Every key has to be unique.`,
        key,
        label,
      };
    }

    const computed = evaluateFormula(formula, scope);

    if (!computed.ok) {
      return { derived, display: '', error: computed.error, key, label };
    }

    scope[key] = computed.value;

    return {
      derived,
      display: formatResultValue(computed.value, toResultFormat(result)),
      key,
      label,
      value: computed.value,
    };
  });
};
